import { Box, Grid, Typography } from '@material-ui/core';
import { GetAppOutlined } from '@material-ui/icons';
import { useStore } from '@roc/client-portal-shared/hooks';
import { useUserStore } from '@roc/feature-app-core';
import { Button } from '@roc/ui';
import { observer } from 'mobx-react';
import { useState } from 'react';
import { QuoteStep } from '../../utils/constants';
import { StepTitle } from '../common/stepTitle';
import { BorrowerInformationCard } from './borrowerInformationCard';
import { DownloadQuoteDialog } from '../common/downloadQuoteDialog';
import { EconomicsSummaryCard } from './economicsSummaryCard';
import { EntityInformationSummaryCard } from './entityInformationSummaryCard';
import { FixFlipPropertyInformationForm } from './fixFlipPropertyInformationForm';
import { PropertyInformationCard, PropertyInformationFormProps } from './propertyInformationCard';
import { hasAtLeastOneNotFilledFee, isBridgeLoan } from '../../utils/utils';
import { IQuoteTypeStore } from '../../stores/common/iQuoteTypeStore';

export interface LoanDetailsCardProps {
  store: IQuoteTypeStore;
}

interface EconomicsStepProps {
  store: IQuoteTypeStore;
  showPointsAndFees?: boolean;
  LoanDetailsCard: React.FC<LoanDetailsCardProps>;
  PropertyInformationForm?: React.FC<PropertyInformationFormProps>;
}

export const SummaryStep = observer(
  ({
    store,
    showPointsAndFees = true,
    LoanDetailsCard,
    PropertyInformationForm = FixFlipPropertyInformationForm,
  }: EconomicsStepProps) => {
    const [showDownloadQuoteDialog, setShowDownloadQuoteDialog] = useState(
      false
    );
    const { userStore } = useUserStore();
    const borrowerFormStores = store.borrowersStore.borrowerFormStores;
    const propertyFormStores = store.propertiesStore.propertyFormStores;

    const { globalStore } = useStore();
    const hasEntity = !!store.entityStore.entityFormStore.form.fields.hasEntity
      .value;

    const allowLoanPricing = userStore.allowLoanPricing;
    const isDownloadDisabled = hasAtLeastOneNotFilledFee(store.loanEconomicsStore.getLoanEconomics());

    return (
      <>
        <Grid container spacing={2}>
          <Grid item xs={12}>
            <StepTitle
              title={allowLoanPricing ? 'Quote Summary' : 'Summary'}
              subtitle={
                allowLoanPricing ? (
                  <>
                    Your estimated quote is ready. Review the borrower, property, and loan details below.
                  </>
                ) : (
                  <></>
                )
              }
            />
          </Grid>
          <Grid item xs={12}>
            {hasEntity ? <EntityInformationSummaryCard /> : null}
          </Grid>
          {borrowerFormStores.map(borrowerFormStore => (
            <Grid item xs={12} key={borrowerFormStore.key}>
              <BorrowerInformationCard
                borrowerFormStore={borrowerFormStore}
                onDeleteClick={() =>
                  store.borrowersStore.removeBorrower(borrowerFormStore.key)
                }
                showActions={false}
                displayPGField={false}
              />
            </Grid>
          ))}
          {propertyFormStores.map(propertyFormStore => (
            <Grid item xs={12} key={propertyFormStore.key}>
              <PropertyInformationCard
                store={store}
                propertyFormStore={propertyFormStore as any}
                PropertyInformationForm={PropertyInformationForm}
                showActions={false}
              />
            </Grid>
          ))}
          <Grid item xs={12}>
            <LoanDetailsCard store={store} />
          </Grid>
          <Grid item xs={12}>
            <EconomicsSummaryCard
              store={store}
              showPointsAndFees={showPointsAndFees}
            />
          </Grid>
          <Grid item xs={12}>
            <Box display="flex" flexDirection="column" alignItems="center">
              <Button
                size="medium"
                variant="outlined"
                color="primary"
                startIcon={<GetAppOutlined />}
                testId="edit-borrower"
                onClick={() => setShowDownloadQuoteDialog(true)}
                disabled={isDownloadDisabled}
              >
                DOWNLOAD QUOTE
              </Button>
              {isDownloadDisabled && (
                <Typography variant='body2' align='center' style={{ marginTop: '16px', color: '#5E5E5E' }}>
                  Download is available when all fees are filled out on the previous page
                </Typography>
              )}
            </Box>
          </Grid>
        </Grid>
        <DownloadQuoteDialog
          store={store as any}
          open={showDownloadQuoteDialog}
          onClose={() => setShowDownloadQuoteDialog(false)}
        />
      </>
    );
  }
);
